import { Fragment, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BatchImageDrop } from '@/components/BatchImageDrop'
import { BatchTable } from '@/components/BatchTable'
import { Icon } from '@/components/icons'
import { ModelSelect } from '@/components/ModelSelect'
import { RefUploader } from '@/components/RefUploader'
import { ModulePicker, PriceConfirmDialog, ShotMixPicker, StudioPanel, SubmitBar } from '@/components/studio'
import {
  Button,
  Card,
  CardBody,
  CardHead,
  CountInput,
  Field,
  GroupedSelect,
  Input,
  Segmented,
  Select,
} from '@/components/ui'
import {
  batchCsvTemplate,
  parseBatchCsv,
  rowsToCsv,
  rowsToPromptsText,
  validateRows,
  type BatchRow,
  type RowIssue,
} from '@/config/batchCsv'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { DEFAULT_PLATFORM_ID, PLATFORM_GROUPS, getPlatform } from '@/config/platforms'
import { sizeLabel, type DetailSpec } from '@/config/platformDetails'
import {
  DEFAULT_MODE,
  STUDIO_MODES,
  getMode,
  type StudioModeId,
} from '@/config/studioModes'
import {
  MAX_BATCH_COUNT,
  clampCount,
  countOf,
  defaultMainMix,
  defaultsFor,
  flatChoices,
  moduleCountsOf,
  normalizeMix,
  ratioChoicesFor,
  shapeOf,
  sizeChoicesFor,
  specFor,
  type MainMix,
  type Tier,
} from '@/config/studioShape'
import { fallbackPrompt, promptsForProduct, refOrder, type ProductInfo, type PromptCtx } from '@/lib/detailPrompts'
import type { RowKey } from '@/lib/batchImages'
import { saveText } from '@/lib/download'
import { refillFromInputs } from '@/lib/refill'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useDraft } from '@/hooks/useDraft'
import { usePricePreview } from '@/hooks/usePricePreview'
import { useJobCenter, type Job, type SubmitArgs } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { useToast } from '@/context/ToastContext'

/**
 * ===========================================================================
 * 批量出图工作台（`/batch`）—— 一份表格 + 一堆图 → N 个任务
 * ===========================================================================
 *
 * ## 这一页为什么存在（2026-09-29 用户原话）
 *
 *   「不过 csv 最大的问题是不带图片，真的搞笑」
 *   「所以我觉得批量处理这个需要新页面，这样一来，我可以后补图片，
 *     在这个页面继续改不现实」
 *
 * 两句说的是同一件事，而且**推翻了上一轮的结论**。上一轮把批量做成了
 * `/detail-page` 页签① 里的一个「输入形态」（Segmented 一切，表单变表格，
 * 宽表挂在 Drawer 里），理由是「批量不是新决策域，只是产品信息从哪来不一样」——
 * 那条判断只看了一个维度（**内容**），漏了另外两个：
 *
 *   1. **寿命不同**。详情页的形态是 `useState`，刷新即失、切页即失。
 *      而「我手里有 80 个产品，今天只传完 40 张图，明天接着传」是**跨天**的活。
 *      一个刷新就清零的东西，装不下它。
 *   2. **输入物相反**（本项目铁律 6 明文允许「输入物相反的独立成页」）。
 *      详情页输入「一个产品的四个字段」，产出 1 个任务；
 *      这一页输入「一份表格 + 一个文件夹的图」，产出 N 个任务。
 *      两者在界面上也没有一寸是共用的 —— 表格 9 列，塞进详情页左栏就是一条
 *      横向滚动条。
 *
 * 所以这一页是**工作台**，不是「详情页的批量模式」。详情页页签① 只留一行
 * `Link` 指路（不留可点的切换钮 —— 那是「同一字段两个入口」，两边状态还不同步）。
 *
 * ---------------------------------------------------------------------------
 * ## 它跟详情页的关系：**同一套零件，不同的家务**
 * ---------------------------------------------------------------------------
 *
 * 产出形态、平台规格、逐屏解构、提示词组装、报价口径、批次提交**全部是同一份**：
 *   · `config/studioShape.ts` —— 形态层（张数 / 档位 / 默认值 / 逐张解构）
 *   · `lib/detailPrompts.ts`   —— 提示词层（产品信息 → 提示词，全站唯一入口）
 *   · `config/batchCsv.ts`     —— 表格层（列清单 / 解析 / 校验 / 模板）
 * 这三层都是**为了这一页上提**的。留在详情页文件里的话，这一页只有两条路：
 * 把整页 import 进来，或者各写一份 —— 后者的后果不是报错，而是
 * 「详情页说这批 4 张、批量页说 6 张」，两边都能提交，报价各按各的算。
 *
 * ---------------------------------------------------------------------------
 * ## 首屏顺序：**导入在前，参数折起来**（2026-09-29 用户口径）
 * ---------------------------------------------------------------------------
 *
 *   「如果是批量的话，这不需要了，把 CSV 的弄进来，然后缺的在表格补就可以，
 *     然后点击批量生成」
 *
 * 批量这一页的活就是「一份表进来、一批任务出去」，所以首屏从上到下**就是
 * 这个流程本身**，不再有别的：
 *
 *   ① 一行**参数摘要**（形态 · 平台 · 比例 · 每产品张数 · 模型单价）。
 *      它不再是主角 —— 原来那块展开的参数网格占掉整屏，而首屏该给导入。
 *      点「改参数」才展开，展开区仍是那一套控件（唯一入口没变）。
 *   ② **导入区**：拖 CSV 进来 / 选文件 / 下载模板 —— 这一页的第一件事。
 *   ③ **表格**：缺什么在这一行里直接补，改完即时复查；**参考图也在这一格里配**。
 *   ④ **右栏顶部**的提交栏：价格与生成按钮同一行，点它批量生成
 *      （2026-10-08 全站统一；它一度在左栏最下面，见 global.css 里那段沿革）。
 *
 * ---------------------------------------------------------------------------
 * ## 图片归位走过三段，最后落在「表格里那一格」（2026-09-30）
 * ---------------------------------------------------------------------------
 *
 *   「不过 csv 最大的问题是不带图片」→ 第一版做成**独立一张卡**（拖一批图 +
 *   按文件名归位 + 未归位清单），摆在右栏；第二版跟着表格搬到左栏；
 *   第三版（用户圈着「参考图」那一列说「可以提供图片上传」、又圈着那张卡写
 *   「删除」）改成：**每行的「参考图」格自己就是上传入口**
 *   （`BatchTable` → `RefUploader wrap="cell"`）。
 *
 * 为什么这是对的，而不只是照做：
 *   · 图本来就是**行的属性**（`row.images`），把入口放在行上，指向性最强 ——
 *     用户看着第 3 行，就点第 3 行那一格，没有「先选一批、再猜归哪行」这一步；
 *   · 那张卡与表格是**同一件事的两个视角**，分开摆就要在两处之间对号
 *     （眼睛盯表格第 8 列，手在下面那张卡上操作）。
 *
 * 「拖一批图按文件名归位」**没有丢，但不再占版面**：它是表格外面那层
 * `BatchImageDrop` 外壳（拖到清单上即触发，认不出的才出现一条清单）。
 * 理由是不想为了一个 1 次动作的批量场景，让 20 次动作的单行场景每次都先路过它。
 *
 * ⚠️ 两条路写的是**同一份行数据**（都经 `applyImages` → 一次 `patch`）。
 * 往某一行的格子里拖时，格子会 `stopPropagation` —— 不然同一张图会被传两遍
 * （一遍给这一行，一遍再按文件名猜一次）。
 *
 * ---------------------------------------------------------------------------
 * ## 两件事刻意**不改**
 * ---------------------------------------------------------------------------
 *
 * 1. **任务键与详情页同源**：提交写 `sourcePage: 'detail-page'`，
 *    右栏读 `jobsFor('detail-page', appSlug)`。
 *    理由：批量出的就是「电商主图 / 平台详情模块 / 模仿版式」这批素材，
 *    （商品套图 2026-09-30 下架，见 `config/studioModes.ts`）
 *    与详情页同一档的活儿本来就是一件事 —— 分开记会变成
 *    「在详情页看不到批量跑的那 20 张」。铁律「读键写键同源」在这里是**两边都写
 *    同一个字面量**，`smoke-nav` 的通用循环会逐页把守（含第二个 appSlug 参数）。
 * 2. **出图参数不接受按行覆盖**。`config/batchCsv.ts` 里有一张
 *    `FORBIDDEN_HEADERS`，CSV 里出现 `ratio` / `outputs` / `model` 这类列一律报错。
 *    同一批必须同规格，否则「这一批多少钱」都没法算 —— 见那一层的说明。
 *
 * ---------------------------------------------------------------------------
 * ## 状态全在草稿里（这是本页的立身之本）
 * ---------------------------------------------------------------------------
 *
 * `useDraft('batch', ...)` 把**参数 + 整张表 + 每行的参考图 + 导入时的文件级问题**
 * 一起落盘（400ms 防抖）。没有这一层，「关掉页面明天回来接着补图」就是空话，
 * 而那是这一页存在的第一个理由（见上）。
 *
 * 逐行的 `issues` / `ok` **不进草稿** —— 它们是 `validateRows()` 的派生物，
 * 行内改一个字就要重算一遍。存起来就会出现「改完了、状态还写着缺产品名」
 * 这种自相矛盾（导入时拦一次、复查时拦另一次，正是本项目踩过的坑）。
 */

const MAX_REFS = 6

/**
 * 草稿形状。
 *
 * ⚠️ 必须是 `type` 而不是 `interface`：`useDraft<T extends Record<string, unknown>>`
 * 靠 TS 对**类型别名**的隐式索引签名才成立，换成 interface 会直接编译不过。
 */
type BatchDraft = {
  /** 产出形态 —— 与详情页同一份真源（`config/studioModes.ts`） */
  mode: StudioModeId
  platform: string
  tier: Tier
  withMobile: boolean
  ratio: string
  /**
   * 张数 —— 与 `/detail-page` 同义，**只有「模仿版式」与「平台详情模块」两档读它**。
   * 「电商主图」档的张数是 `mainMix` 之和（见那里），这一档不渲染数字输入框。
   */
  outputs: number
  /** 主图配比（只有「电商主图」档读）—— 与详情页同一份语义、同一个控件 */
  mainMix: MainMix
  /**
   * 官方模块 → **份数**（只有「平台详情模块」档读；0 = 不出）。
   *
   * 与 `/detail-page` **同一份语义、同一个控件**（2026-10-08 一起从「勾选名单」
   * 换成份数）：两个页面各存一种表示的话，`shapeOf` 就得认两种，而它只认一种 ——
   * 另一边会静默退回默认值。
   */
  moduleCounts: Record<string, number>
  /**
   * **整批统一尺寸**（只对「平台详情模块」档有意义，与详情页同一份语义）。
   * 空字符串 = 官方尺寸逐张匹配（默认）。
   */
  batchSize: string
  resolution: '1k' | '2k' | '4k'
  model: string
  /**
   * **参考版式图**（只有「模仿版式」这一档要）。
   *
   * 它是**批次级**的 —— 一整批 20 个产品沿用同一套版式，所以放在参数里、
   * 不放进每一行（行里的 `images` 是各产品自己的产品图）。
   * 与 `/detail-page` 的同名字段逐字同义，两页的提交载荷都走 `refOrder()`
   * 把「产品图在前、版式图在后」这个顺序钉死。
   *
   * 2026-10-07 补：这一档此前在批量页**没有入口**（形态清单里有「模仿版式」，
   * 但整页找不到上传版式的地方），提交出去的提示词只能写「本次没有参考版式图」——
   * 用户按形态的字面意思选，拿到的却是另一件事。
   */
  layoutRefs: string[]
  /**
   * 表格本体 —— 只存 `{ 文件行号, 产品信息 }`。
   * `sku` 为空时解析层会补成 `#1` / `#2`…，所以这里拿得到稳定的行标识。
   */
  rows: { line: number; row: BatchRow }[]
  /**
   * **导入那一刻**的文件级问题（缺必填列 / 混进了出图参数列 / 空文件）。
   *
   * 它必须进草稿而不是只在 `useState` 里活一次：里面有一条是
   * 「你这一列是出图参数，已经拒收」—— 那一列**已经被丢掉了**，
   * 刷新之后如果提示也没了，用户永远不知道自己少传了一列。
   */
  fileIssues: RowIssue[]
}

const DEFAULT_PROFILE = getPlatform(DEFAULT_PLATFORM_ID)
const D0 = defaultsFor('main', DEFAULT_PROFILE)

/** 空表 —— 一行都没有时也要能「加一行」，所以初始给一行空行 */
const emptyRow = (line: number): { line: number; row: BatchRow } => ({
  line,
  row: { sku: `#${line - 1}`, productName: '', sellingPoints: '', images: [] },
})

const DEFAULTS: BatchDraft = {
  mode: DEFAULT_MODE,
  platform: DEFAULT_PROFILE.id,
  tier: 'basic',
  withMobile: false,
  ratio: D0.ratio,
  outputs: D0.outputs,
  mainMix: defaultMainMix(),
  moduleCounts: {},
  batchSize: '',
  resolution: '2k',
  model: DEFAULT_FAMILY_ID.image,
  layoutRefs: [],
  rows: [emptyRow(2)],
  fileIssues: [],
}

export function BatchStudio() {
  const [sp, setSp] = useSearchParams()
  const { value: form, patch, reset } = useDraft('batch', DEFAULTS)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  /**
   * ⚠️ `workspace` 只在这两处传（另一处是各单页）—— 批量页的**可编辑内容也包括那张表**
   * （`form.rows`）。快照取的是**整个 `form`**，所以「生成成功后自动清空」会连表一起清，
   * 与用户口径「左栏表单内容」一致（2026-10-09）。指纹比对见 `useGenerateFlow` 的看门狗：
   * 跑批这几十秒里用户又改了表，就不清 —— 用户的改动永远赢。
   */
  const flow = useGenerateFlow({ workspace: { snapshot: form, reset } })
  const toast = useToast()
  const csvRef = useRef<HTMLInputElement | null>(null)
  const { list: listModels } = useModels()

  /**
   * 参数条的展开态。
   *
   * ⚠️ **不进草稿** —— 它是「我正要看一眼」，不是这一批的数据。
   * 存进草稿会让「上次点开过」跟着跨天，而它与产出没有任何关系。
   * （对照：整张表与每行的图都在草稿里，那些才是用户要接着改的东西。）
   */
  const [paramOpen, setParamOpen] = useState(false)

  /** CSV 拖拽经过时的高亮态 —— 纯视觉，不进任何状态机 */
  const [csvOver, setCsvOver] = useState(false)

  /* ------------------------------ 形态与参数 ------------------------------ */

  const activeMode = getMode(form.mode)
  const mode = activeMode.id
  const isAplus = mode === 'aplus'

  const profile = getPlatform(form.platform)
  const detail = specFor(profile, form.tier)
  const activeName = mode === 'aplus' ? detail.tabName : getMode(mode).name

  /** 模块份数的完整表（按当前平台规格归一）—— 与 `/detail-page` 同名同义 */
  const moduleCounts = useMemo(
    () => moduleCountsOf(detail, form.moduleCounts),
    [detail, form.moduleCounts],
  )
  /** 份数 > 0 的模块名（官方顺序） */
  const pickedModules = useMemo(
    () => detail.modules.filter((m) => (moduleCounts[m.name] ?? 0) > 0).map((m) => m.name),
    [detail, moduleCounts],
  )

  const { shots, plannedShots } = useMemo(
    () =>
      shapeOf({
        mode,
        outputs: form.outputs,
        ratio: form.ratio,
        detail,
        withMobile: form.withMobile,
        batchSize: form.batchSize,
        // 与 `/detail-page` 传的是同一对字段 —— 两页各传一份的话，
        // 「同样的配比，单张出 7 张、批量出 4 张」这种差别不会报错，只是对不上
        mix: form.mainMix,
        modules: pickedModules,
        counts: moduleCounts,
      }),
    [
      mode,
      form.outputs,
      form.ratio,
      form.mainMix,
      pickedModules,
      moduleCounts,
      detail,
      form.withMobile,
      form.batchSize,
    ],
  )
  const batchCount = countOf(mode, form.outputs, shots)

  /**
   * 当前模型**真正能出的比例** —— 与 `/detail-page` 同一份真源（后端下发的
   * `capabilities.aspect_ratios`）。批量页也放同样的候选，否则「同一个产品，
   * 单张出得了 4:5、批量出不了」这种差别会让人以为是 bug。
   */
  const modelRatios = useMemo(
    () => listModels('image').find((m) => m.id === form.model)?.capabilities?.aspect_ratios,
    [listModels, form.model],
  )
  const ratioChoices = useMemo(
    () => ratioChoicesFor(mode, profile, modelRatios),
    [mode, profile, modelRatios],
  )
  /**
   * 头部那句「已选 N/M」—— 派生量，不另存 state。
   *
   * ⚠️ `slotLimit`（「超出就从头循环」那句提示的底数）随份数一起删掉了：
   * 有了份数就**不存在「超过」** —— 想多要一轮就把那个模块 +1。
   */
  const moduleCount = pickedModules.length
  /** 输出尺寸候选项 —— 只有「平台详情模块」档用（与详情页同一个真源、同一个控件） */
  const sizeChoices = useMemo(
    () => sizeChoicesFor(detail, profile, modelRatios),
    [detail, profile, modelRatios],
  )

  /**
   * 草稿里可能躺着非法张数（旧版本存过别的上限，或手改过 localStorage）——
   * 夹一次并回写。理由与 `/detail-page` 那处逐字相同：不回写的话
   * `planDetailShots` 会照着它规划，报价按 999 张算、清单也真有 999 行。
   */
  useEffect(() => {
    const fixed = clampCount(form.outputs)
    if (fixed !== form.outputs) patch({ outputs: fixed })
  }, [form.outputs, patch])

  /**
   * 主图配比同样夹一次并回写 —— 理由与 `/detail-page` 那处逐字相同：
   * 草稿里可能是上一版长度的配比或手改过的 `[-3, "2"]`，不收敛会让报价算成
   * `NaN`、逐张清单行数与「当前共 N 张」对不上。
   */
  useEffect(() => {
    const fixed = normalizeMix(form.mainMix)
    if (fixed.join(',') !== (form.mainMix ?? []).join(',')) patch({ mainMix: fixed })
  }, [form.mainMix, patch])

  /**
   * 摘要条上那个模型名 —— 从 `ModelsContext` 取，**不自己维护一张 id→名称表**。
   * 模型清单是上行下发的（能力真源在后端 `registry.py`），页面里再写一份名字
   * 就会出现「下拉里叫 A、摘要里叫 B」，而用户没法判断哪个是真的。
   */
  const curModel = useMemo(() => {
    const all = listModels('image')
    return all.find((m) => m.id === form.model) ?? all[0] ?? null
  }, [listModels, form.model])

  /**
   * 把当前形态同步进地址栏 `?mode=`。
   *
   * 为什么**这一页写**、`/detail-page` 只读不写（同一天、同一个参数名）：
   *   · `/detail-page` 的同名参数是**一次性入站指令**（老书签 `/ai-set` 进来铺一次
   *     形态就摘掉，消费完 `delete('mode')`），页面内的形态切换不写回去 ——
   *     写回去会与那个消费 effect 互相翻译同一份状态。
   *   · 这一页没有「入站指令」这回事（`/batch` 不是任何老地址的落点），
   *     参数在这里就是**当前状态**。写进地址栏有两条实打实的收益：
   *       ① `appSlugFromLocation()` 靠它给拖进来的图**归类**
   *          （在 `/batch` 传的参考图该记成 `detail-main` 还是 `ai-set`）。
   *          不写的话上传一律掉进兜底的 `other` 分类 —— 界面上看不见，
   *          但素材库会脏，而且是不可逆的脏。
   *       ② 链接可分享：这一页没有别名路由可以承载「带形态进来」。
   */
  useEffect(() => {
    const cur = sp.get('mode') ?? ''
    if (cur === mode) return
    const next = new URLSearchParams(sp)
    next.set('mode', mode)
    setSp(next, { replace: true })
  }, [mode, sp, setSp])

  /**
   * 入站形态：**只认一次**。
   *
   * 分享出去的 `/batch?mode=aplus` 进来要落在这个形态上，而不是被草稿里那个
   * `main` 覆盖掉（草稿是「我上次用的」，地址栏是「我要去哪」，后者优先一次）。
   * 之后再切形态，地址栏跟着表单走（上面那个 effect）—— 两个方向不打架。
   */
  const adopted = useRef(false)
  useEffect(() => {
    if (adopted.current) return
    adopted.current = true
    const raw = sp.get('mode')
    if (!raw) return
    const target = getMode(raw)
    // 手改地址栏塞了个不存在的 id：`getMode` 已兜底成默认形态，这里不动表单
    if (target.id !== raw || target.id === form.mode) return
    const d = defaultsFor(target.id, profile, { detail, withMobile: form.withMobile })
    patch({
      mode: target.id,
      outputs: d.outputs,
      ratio: target.id === 'aplus' ? form.ratio : d.ratio,
      mainMix: target.id === 'main' ? defaultMainMix() : form.mainMix,
      moduleCounts: moduleCountsOf(detail, form.moduleCounts),
    })
    // 只在挂载时认一次：deps 里放 form/profile 会让用户每次改参数都重跑一遍，
    // 把形态按回地址栏里的旧值
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ------------------------------ 表格 ------------------------------ */

  /**
   * 逐行校验 —— **派生**，不是状态。
   * 行内改一个字就重算一遍，与导入时用的是同一个 `validateRows`。
   */
  const parsed = useMemo(() => validateRows(form.rows), [form.rows])
  const usable = useMemo(() => parsed.filter((r) => r.ok), [parsed])
  const badCount = parsed.length - usable.length
  // 参考图总数 = 各行的产品图 + **每行都会带上**的那几张版式图（模仿版式档）
  const refImageTotal = useMemo(
    () => usable.reduce((n, r) => n + r.row.images.length + form.layoutRefs.length, 0),
    [usable, form.layoutRefs.length],
  )

  /** 能接收图片的行 —— 有 error 的行收了图也出不了（见 `BatchImageDrop` 的契约） */
  const rowKeys = useMemo<RowKey[]>(
    () => usable.map((r) => ({ line: r.line, sku: r.row.sku, productName: r.row.productName })),
    [usable],
  )

  /** 行自增的**唯一取号处**：取当前最大行号 + 1，删行不回收号码（否则删完再增会撞号） */
  const nextLine = () => form.rows.reduce((m, r) => Math.max(m, r.line), 1) + 1

  const editRow = (line: number, p: Partial<BatchRow>) =>
    patch({ rows: form.rows.map((r) => (r.line === line ? { ...r, row: { ...r.row, ...p } } : r)) })

  const addRow = () => {
    const line = nextLine()
    patch({ rows: [...form.rows, emptyRow(line)] })
    toast.toast(`加了第 ${line - 1} 行`)
  }

  const removeRow = (line: number) => {
    // 最后一行不删：删空之后连「加一行」都不会点，页面变成一片空白
    if (form.rows.length <= 1) return
    patch({ rows: form.rows.filter((r) => r.line !== line) })
  }

  /**
   * 补图 —— 归位结果合并进行里。
   *
   * 三条：**追加不清空**（用户是分几次传的，这正是本页要支持的方式）、
   * **去重**（同一张拖两次不该出现两遍）、**封顶**（与详情页的上传器同一个上限
   * `MAX_REFS`，超了就截断并说一声，不静默丢）。
   */
  const applyImages = (byLine: Map<number, string[]>) => {
    let capped = 0
    const rows = form.rows.map((r) => {
      const add = byLine.get(r.line)
      if (!add?.length) return r
      const merged = [...r.row.images]
      for (const u of add) {
        if (merged.includes(u)) continue
        if (merged.length >= MAX_REFS) {
          capped += 1
          continue
        }
        merged.push(u)
      }
      return { ...r, row: { ...r.row, images: merged } }
    })
    patch({ rows })
    if (capped) toast.error(`有 ${capped} 张超出行上限 ${MAX_REFS}，没放进去 —— 每行最多 ${MAX_REFS} 张参考图`)
  }

  /* ------------------------------ 导入 / 导出 ------------------------------ */

  const importCsv = async (file: File) => {
    const text = await file.text()
    const res = parseBatchCsv(text)
    if (res.empty && !res.rows.length) {
      // 解析本身失败（空文件 / 只有表头）：**不动**现有表格，只报问题
      patch({ fileIssues: res.fileIssues })
      toast.error(res.fileIssues[0]?.text ?? '这份 CSV 里没读到数据行')
      return
    }
    /*
     * ⚠️ 替换整张表 —— 包括**每行已有的参考图**。
     *
     * 导入是一份新台账，那批图已经跟着旧行走了。保留旧图只会出现
     * 「第 3 行的图是上一份表第 3 行的」这种串行错误，而且看不出错。
     * 所以导入前若表里已经有图，先问一句。
     */
    if (form.rows.some((r) => r.row.images.length)) {
      const ok = window.confirm('导入会替换整张表，表格里已归位的参考图会一起清掉。继续？')
      if (!ok) return
    }
    patch({ rows: res.rows.map((r) => ({ line: r.line, row: r.row })), fileIssues: res.fileIssues })
    const bad = res.rows.filter((r) => !r.ok).length
    if (bad) toast.toast(`导入 ${res.rows.length} 行，其中 ${bad} 行待修 —— 见表格里的红字`)
    else toast.success(`导入 ${res.rows.length} 行，全部可用`)
  }

  const exportCsv = () => {
    saveText('批量出图-产品表.csv', rowsToCsv(form.rows.map((r) => r.row)), 'text/csv')
  }

  const exportPrompts = () => {
    const items = usable.map((r) => ({ row: r.row, prompts: promptsFor(r.row, r.row.images.length) }))
    if (!items.length) {
      toast.error('还没有可用的行 —— 先填上产品名称')
      return
    }
    saveText('批量出图-提示词.txt', rowsToPromptsText(items))
  }

  /**
   * CSV 拖进导入区。
   *
   * 拖进来的**多半是图**：这一页有两个拖拽区（左边收表、右栏收图），
   * 而「把一批图拖到表格上」是个很自然的动作。所以这里必须**明确指路**，
   * 不能静默忽略 —— 静默忽略的后果是用户以为「图已经进去了」，
   * 一直要到提交前才发觉每行都没有参考图（而这正是这一页存在的理由）。
   */
  const onCsvDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setCsvOver(false)
    const files = Array.from(e.dataTransfer.files)
    if (!files.length) return
    const csv = files.find(
      (f) => /\.(csv|txt)$/i.test(f.name) || f.type === 'text/csv' || f.type === 'text/plain',
    )
    if (!csv) {
      toast.error('这里是放表格的 —— 图片请拖到右栏「参考图归位」')
      return
    }
    if (files.length > 1) toast.toast(`一次只读一份表，已读 ${csv.name}`)
    void importCsv(csv)
  }

  /* ------------------------------ 提示词 ------------------------------ */

  /**
   * 提示词上下文 —— 与产品无关的那部分，**全批次只有一份**。
   *
   * 交付形态（`mode` / 平台 / 比例 / 张数）来自页面上方那条参数带，
   * 不来自表格：表格里出现这几列会被 `config/batchCsv.ts` 直接拒收。
   */
  const promptCtx = useMemo<PromptCtx>(
    () => ({
      profile,
      detail,
      mode,
      modeName: activeName,
      ratio: form.ratio,
      resolution: form.resolution,
      outputs: Math.max(1, form.outputs),
      batchSize: form.batchSize,
      shots,
      plannedShots,
      layoutRefCount: form.layoutRefs.length,
    }),
    [profile, detail, mode, activeName, form.ratio, form.resolution, form.outputs, form.batchSize, shots, plannedShots, form.layoutRefs.length],
  )

  /**
   * 一行 → 产品信息。
   *
   * ⚠️ 2026-09-30：`category` / `scene` 两项随「商品套图」一起删除
   * —— 它们只被套图提示词读过，而那条路径已整块下架。
   * 现在一行只有「产品名 + 卖点」，与 CSV 的列一一对应。
   */
  const infoOf = (row: BatchRow): ProductInfo => ({
    productName: row.productName,
    sellingPoints: row.sellingPoints,
  })

  /** 一行 → 逐张提示词。**与详情页调的是同一个函数**（`promptsForProduct`） */
  const promptsFor = (row: BatchRow, refCount: number) => promptsForProduct(promptCtx, infoOf(row), refCount)

  /* ------------------------------ 报价与提交 ------------------------------ */

  const price = usePricePreview({
    appSlug: activeMode.appSlug,
    model: form.model,
    outputs: Math.max(1, batchCount * usable.length),
    resolution: form.resolution,
    aspectRatio: shots[0]?.size ?? form.ratio,
  })

  /**
   * 组装**一个任务**的提交参数（一行产品一条）。
   *
   * `inputs` 是**显式列字段**，不是 `...form` —— 详情页那份 form 是平的（十几个
   * 产品/出图字段），这一页的 form 里躺着**整张表**。一个 `...form` 就会把
   * 80 行的表格塞进**每个任务**的 inputs 里落库，还进提示词预览。
   */
  const buildArgs = (line: number, row: BatchRow): SubmitArgs => {
    const p = infoOf(row)
    const rowPrompts = promptsForProduct(promptCtx, p, row.images.length)
    return {
      appSlug: activeMode.appSlug,
      title: `${profile.name} ${activeName} · ${p.productName || row.sku}`,
      // 与 jobsFor('detail-page') 同键：批量出的就是这一档素材，
      // 分开记会变成「在详情页看不到批量跑的那 20 张」
      sourcePage: 'detail-page',
      model: form.model,
      prompt: rowPrompts[0] ?? fallbackPrompt(promptCtx, p, row.images.length),
      prompts: rowPrompts,
      // 逐张尺寸（A+ 各模块官方尺寸不同）—— 与 prompts 一一对应
      ...(shots.length ? { ratios: shots.map((s) => s.size) } : {}),
      inputs: {
        __title: activeName,
        mode,
        platform: profile.id,
        tier: form.tier,
        withMobile: form.withMobile,
        ratio: form.ratio,
        outputs: form.outputs,
        // 整批统一尺寸（空 = 官方尺寸逐张匹配）—— 重试时要能恢复同一批参数
        batchSize: form.batchSize,
        resolution: form.resolution,
        model: form.model,
        // 行的标识 —— 失败时用户靠它在 80 行里找到是哪一行
        sku: row.sku,
        batchLine: line,
        productName: p.productName,
        sellingPoints: p.sellingPoints,
      },
      imageUrls: refOrder(row.images, form.layoutRefs),
      aspectRatio: shots[0]?.size ?? form.ratio,
      platform: profile.id,
      resolution: form.resolution,
      outputs: batchCount,
      estimatedMs: 45_000 * batchCount,
    }
  }

  const doGenerate = () => {
    if (!usable.length) return
    flow.request({
      info: {
        title: `${profile.name} · ${activeName}`,
        appSlug: activeMode.appSlug,
        model: form.model,
        platform: profile.name,
        aspectRatio: shots[0]?.size ?? form.ratio,
        resolution: form.resolution,
        price: price?.estimatedPrice ?? 0,
        basis: price?.basis ?? `${usable.length} 个产品 × ${batchCount} 张`,
        free: price?.isFreeThisCall,
        // 总张数：报价弹窗要拿它跟「N 个产品」对得上
        outputs: batchCount * usable.length,
        refImages: refImageTotal,
        prompt: '',
        /*
         * `batch` 一出现，弹窗就从「一份提示词」切成「逐行清单 + 逐行词」。
         * 逐行 `prompt` 是**该行全部张拼起来**的（同一行 6 张，不拼起来就只能看第一张）。
         */
        batch: {
          rows: usable.map((r) => ({
            label: `${r.row.sku} · ${r.row.productName}`,
            outputs: batchCount,
            prompt: promptsForProduct(promptCtx, infoOf(r.row), r.row.images.length).join('\n\n'),
          })),
        },
      },
      args: usable.map((r) => buildArgs(r.line, r.row)),
    })
  }

  /* ------------------------------ 右栏 ------------------------------ */

  const jobs = jobsFor('detail-page', activeMode.appSlug)
  const outputs = outputsFor('detail-page', activeMode.appSlug)
  const sizesInBatch = useMemo(() => [...new Set(shots.map((s) => s.size))], [shots])

  const submitLabel = usable.length ? `生成这 ${usable.length} 个产品` : '生成'
  /**
   * 用户**动过**的行有几行。
   *
   * 初始那一行空行是**版面占位**（不留白），它不是「用户还没修好的行」。
   * 把两者混起来说「表格里有 1 行还没修好」，会让人一进门就以为自己做错了什么 ——
   * 而他其实只是还没开始。CDP 验收在这里抓到过一次（禁用原因该指向「导入 CSV」）。
   */
  const touchedRows = useMemo(
    () => parsed.filter((r) => r.row.productName.trim() || r.row.sellingPoints.trim() || r.row.images.length).length,
    [parsed],
  )
  /**
   * 不可提交的原因 —— **每一种都要有对应的那句话**（与 `/detail-page` 同一口径）。
   *
   * 「模仿版式」这一档的必填项比别的档多一项：参考版式图（批次级、整批共用）。
   * 不拦的话这一档会照常出图，只是提示词里写着「本次没有参考版式图」——
   * 用户花了钱、拿到的却不是「模仿版式」这件事（页面上那条形态说明还挂在那儿）。
   */
  const disabledReason =
    mode === 'copy' && !form.layoutRefs.length
      ? '「模仿版式」要先上传参考版式图（在「改参数」里，整批共用一份）'
      : usable.length
        ? undefined
        : touchedRows
          ? `表格里有 ${badCount} 行还没修好 —— 看那一行的红字`
          : '表格里还没有产品 —— 导入 CSV、或者先加一行填上产品名称'

  /* ------------------------------ 参数带 ------------------------------ */

  /**
   * 换平台 / 换档位之后把「整批尺寸」收敛回新候选表 —— 与 `/detail-page` 同一份规则。
   * 不收敛的话，下拉会照常显示第一项而 state 里还是旧值：
   * **界面看着改过来了，提交出去是另一个尺寸。**
   */
  const fitBatchSize = (raw: string, nextProfile: typeof profile, nextDetail: DetailSpec) =>
    flatChoices(sizeChoicesFor(nextDetail, nextProfile, modelRatios)).some((o) => o.value === raw)
      ? raw
      : ''

  const switchMode = (id: StudioModeId) => {
    const d = defaultsFor(id, profile, { detail, withMobile: form.withMobile })
    patch({
      mode: id,
      outputs: d.outputs,
      ratio: id === 'aplus' ? form.ratio : d.ratio,
      batchSize: fitBatchSize(form.batchSize, profile, detail),
      // 与 `/detail-page` 的 `switchMode` 同一条规矩：换形态 = 换整套默认值
      mainMix: id === 'main' ? defaultMainMix() : form.mainMix,
      moduleCounts: moduleCountsOf(detail, form.moduleCounts),
    })
  }

  const switchPlatform = (id: string) => {
    const next = getPlatform(id)
    const nextTier: Tier = next.detailPremium ? form.tier : 'basic'
    const nextDetail = specFor(next, nextTier)
    const d = defaultsFor(mode, next, { detail: nextDetail, withMobile: form.withMobile })
    patch({
      platform: next.id,
      tier: nextTier,
      outputs: d.outputs,
      ratio: mode === 'aplus' ? form.ratio : d.ratio,
      batchSize: fitBatchSize(form.batchSize, next, nextDetail),
      // 模块表按新平台规格重新归一（旧平台的模块名在 `moduleCountsOf` 里被丢掉）
      moduleCounts: moduleCountsOf(nextDetail, form.moduleCounts),
    })
  }

  const switchTier = (t: Tier) => {
    const nextDetail = specFor(profile, t)
    const d = defaultsFor(mode, profile, { detail: nextDetail, withMobile: form.withMobile })
    patch({
      tier: t,
      outputs: d.outputs,
      ratio: mode === 'aplus' ? form.ratio : d.ratio,
      batchSize: fitBatchSize(form.batchSize, profile, nextDetail),
      // 两档模块清单不同（基本 5 / 高级 7），归一后按新档位补默认份数
      moduleCounts: moduleCountsOf(nextDetail, form.moduleCounts),
    })
  }

  const toggleMobile = (on: boolean) => {
    const d = defaultsFor(mode, profile, { detail, withMobile: on })
    patch({ withMobile: on, outputs: d.outputs })
  }

  /**
   * 出图参数 —— **收成一行摘要，点开才是那套控件**。
   *
   * 2026-09-29 用户口径：「如果是批量的话，这不需要了，把 CSV 的弄进来」。
   * 这句有个容易做反的地方，值得写下来：他说的「不需要」是**它不该占首屏**，
   * 不是「参数可以不要」。比例 / 张数 / 平台 / 模型这一批必须共用一套，
   * 而且必须**只在这一处**出现 —— 摊进表格逐行覆盖的话，「这一批多少钱」
   * 当场就算不出来（`config/batchCsv.ts` 的 `FORBIDDEN_HEADERS` 挡的就是这个）。
   * 所以做法是**折起来**：不删、也不摊到行上。
   *
   * 摘要里那几个值**全部是派生物**，没有一个新 state：形态来自 `activeMode`、
   * 平台来自 `profile`、比例与张数来自 `form`、模型名来自 `ModelsContext`。
   * 存一份「摘要文案」进 state，就等着它跟真值对不上。
   */
  const paramSummary = [
    /*
     * 形态那一段曾经是「一级分组 · 形态」两级拼接，还要去重
     * （`activeGroup.name` 与 `activeName` 经常是同一个词，拼起来是
     * 「电商主图 · 电商主图」）。2026-09-30 一级 chip-row 删除后，
     * 形态只剩一个名字，那段去重逻辑连同分组层一起删掉了。
     */
    activeName,
    profile.name,
    isAplus
      ? form.batchSize
        ? `统一 ${sizeLabel(form.batchSize)} · ${shots.length} 张`
        : `${shots.length} 张按官方尺寸`
      : sizeLabel(form.ratio),
    `每个 ${batchCount} 张`,
    curModel
      ? `${curModel.name}${curModel.unitPrice ? ` ¥${curModel.unitPrice.toFixed(2)}/${curModel.unit}` : ''}`
      : '',
  ].filter(Boolean)

  const paramBar = (
    <Card>
      <div className="batchpage__parambar">
        <span className="batchpage__parambar-icon">
          <Icon name="bolt" size={15} />
        </span>
        <div className="batchpage__parambar-text">
          <div className="batchpage__parambar-line">
            {paramSummary.map((t, i) => (
              <Fragment key={i}>
                {i ? (
                  <span className="batchpage__dot" aria-hidden="true">
                    ·
                  </span>
                ) : null}
                <span>{t}</span>
              </Fragment>
            ))}
          </div>
          <div className="batchpage__parambar-sub">
            这一批共用一套参数，会写成 {usable.length || 0} 个任务、共 {batchCount * usable.length} 张
          </div>
        </div>
        <Button
          size="sm"
          variant={paramOpen ? 'primary' : 'outline'}
          icon="settings"
          aria-expanded={paramOpen}
          onClick={() => setParamOpen((v) => !v)}
        >
          {paramOpen ? '收起参数' : '改参数'}
        </Button>
      </div>

      {paramOpen ? (
        <CardBody>
          <div className="batchpage__params">
              {/*
                「产出方向」（页签外那行一级 chip-row）于 2026-09-30 删除，
                与 `/detail-page` 同步 —— 用户原话「详情页跟主图合并为一页，
                然后在输入的时候选择就可以」。两页共用同一份形态清单
                （`config/studioModes.ts`），所以这一层必须一起删：
                只删详情页那边，批量页就会是「一个页面里四个形态、另一个页面里
                还要先选方向」，同一件事两套交互。
              */}
              <Field label="产出形态" hint={`共 ${STUDIO_MODES.length} 种，切换即换整套参数`}>
                <Segmented
                  value={mode}
                  onChange={(v) => switchMode(v as StudioModeId)}
                  options={STUDIO_MODES.map((m) => ({
                    value: m.id,
                    label: m.id === 'aplus' ? detail.tabName : m.name,
                  }))}
                />
              </Field>

              {/*
                「模仿版式」的参考版式图 —— **批次级**，整批共用一份（与行里的
                产品图是两回事：那个每行各有一张）。

                为什么长在参数抽屉里而不是表格里：表格的一行 = 一个产品，
                而版式图与产品无关 —— 它是这一批的**排版模板**。
                放进行里就会出现「20 行各传一张版式图」这种没有意义的重复。

                ⚠️ 只有这一档渲染它：另外两档的版式由平台规格决定
                （主图四张固定解构 / A+ 逐模块），多一个上传口就是多一个
                「传了也不生效」的入口。
              */}
              {mode === 'copy' ? (
                <div className="batchpage__wide">
                  <RefUploader
                    wrap="none"
                    label="参考版式图"
                    required
                    fieldHint="只取结构与信息层级，不参与产品还原 —— 整批共用这一份（可传一张长图，也可传多张分屏版式）"
                    urls={form.layoutRefs}
                    onChange={(layoutRefs) => patch({ layoutRefs })}
                    max={MAX_REFS}
                    firstBadge=""
                    dropLabel="上传参考版式"
                    dropHint="长图或单张版式 · 可多选 · 逐张对应一张产出"
                  />
                </div>
              ) : null}

              <Field label="投放平台" hint={`主图 ${profile.sizes.main} / 副图 ${profile.sizes.secondary}`}>
                <Select value={form.platform} onChange={(e) => switchPlatform(e.target.value)} aria-label="投放平台">
                  {PLATFORM_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.list.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </Field>

              {isAplus && profile.detailPremium ? (
                <Field label="A+ 档位">
                  <Segmented
                    value={form.tier}
                    onChange={(v) => switchTier(v as Tier)}
                    options={[
                      { value: 'basic', label: '基本 A+' },
                      { value: 'premium', label: '高级 A+' },
                    ]}
                  />
                </Field>
              ) : null}

              {isAplus ? (
                <Field label="移动端资产">
                  <Segmented
                    value={form.withMobile ? 'on' : 'off'}
                    onChange={(v) => toggleMobile(v === 'on')}
                    options={[
                      { value: 'off', label: '不生成' },
                      { value: 'on', label: '生成' },
                    ]}
                  />
                </Field>
              ) : null}

              {isAplus ? (
                <div className="batchpage__wide">
                  <Field
                    label="输出尺寸"
                    hint={
                      flatChoices(sizeChoices).length > 1
                        ? '默认按平台官方模块规格逐张匹配；改成某个比例则本批每一张都用它'
                        : `${detail.tabName} 的官方尺寸就是 ${sizeChoices[0]?.options[0]?.label ?? '—'}`
                    }
                  >
                    <GroupedSelect
                      value={form.batchSize}
                      onChange={(v) => patch({ batchSize: v })}
                      groups={sizeChoices}
                      ariaLabel="输出尺寸"
                    />
                  </Field>
                  {/*
                    逐张清单**保留**：它回答「这一批具体出哪几张、每张多大」，
                    改过尺寸之后数字会跟着变（`shots` 是覆盖后的结果），
                    「改一次全变」看得见。与 `/detail-page` 是同一份呈现。
                  */}
                <Field
                  label="逐张尺寸"
                  hint={
                    form.batchSize
                      ? `本批统一按 ${sizeLabel(form.batchSize)}：${sizesInBatch.map(sizeLabel).join(' / ') || '—'}`
                      : `按官方模块规格逐张匹配，无需手选比例：${sizesInBatch.map(sizeLabel).join(' / ') || '—'}`
                  }
                >
                  <div className="size-list">
                    {shots.map((s, i) => (
                      <div className="size-list__row" key={`${i}-${s.name}`}>
                        <span className="size-list__idx">{i + 1}</span>
                        <span className="grow">{s.name}</span>
                        {s.textFree ? <span className="size-list__tag">无文字</span> : null}
                        <b className="size-list__size">{sizeLabel(s.size)}</b>
                      </div>
                    ))}
                  </div>
                </Field>
              </div>
            ) : (
              <Field label="比例">
                <GroupedSelect
                  value={form.ratio}
                  onChange={(v) => patch({ ratio: v })}
                  groups={ratioChoices}
                  ariaLabel="画面比例"
                />
              </Field>
            )}

            {/*
              每个产品出几张 —— 与 `/detail-page` **同一套口径**：

              · 「电商主图」→ 按类型配比（这一档的张数就是配比之和，没有数字输入框）；
              · 其余两档   → 自己敲数字，超过官方图位数时 `planDetailShots` 循环官方序列。
            */}
            {/*
              数量 —— 分三副面孔，与 `/detail-page` 同一条口径（见那里的长注释）：
              「电商主图」按配比、其余档敲数字；**「平台详情模块」档整块不出**，
              它的张数是下面「包含模块」各模块份数之和（两个入口写同一个量
              迟早对不上，同 §2-3）。
            */}
            {mode === 'aplus' ? null : (
              <Field
                label={mode === 'main' ? '每个产品各类张数' : '每个产品出几张'}
                hint={
                  mode === 'main'
                    ? '四类各出几张；某一类填 0 就是这一类不出。同一批必须同配比，否则算不清这一批多少钱'
                    : '同一批必须同张数，否则算不清这一批多少钱'
                }
              >
                {mode === 'main' ? (
                  <ShotMixPicker mix={form.mainMix} onChange={(m) => patch({ mainMix: m })} />
                ) : (
                  <CountInput
                    value={form.outputs}
                    onChange={(n) => patch({ outputs: n })}
                    max={MAX_BATCH_COUNT}
                  />
                )}
              </Field>
            )}

            {/* 包含模块 —— 与 `/detail-page` 同一个控件、同一份语义（勾选变化顺带设张数） */}
            {isAplus ? (
              <div className="batchpage__wide">
                <Field
                  label="包含模块"
                  hint={`每个模块出几份（0 = 不出），合计 ${batchCount} 张 —— 这一批的每个产品都按这份配比出。当前已选 ${moduleCount} 个模块`}
                >
                  <ModulePicker
                    spec={detail}
                    counts={moduleCounts}
                    withMobile={form.withMobile}
                    onChange={(next) => patch({ moduleCounts: next })}
                  />
                </Field>
              </div>
            ) : null}

              <div className="batchpage__wide">
                <ModelSelect
                  kind="image"
                  value={form.model}
                  onChange={(v) => patch({ model: v })}
                  hasRef={refImageTotal > 0}
                />
              </div>
          </div>
        </CardBody>
      ) : null}
    </Card>
  )

  /* ------------------------------ 表格卡 ------------------------------ */

  const importInput = (
    <input
      ref={csvRef}
      className="batchpage__file"
      type="file"
      // ⚠️ accept 必须只写 CSV/text —— `smoke-nav` 有一条通用扫描：
      // 未声明 accept 或 accept 含 image/video 的 `<input type="file">`
      // 会被判成「自己写的参考图上传器」（参考图只许 RefUploader）。
      accept=".csv,text/csv,.txt,text/plain"
      style={{ display: 'none' }}
      onChange={(e) => {
        const f = e.target.files?.[0]
        if (f) void importCsv(f)
        // 清空 value：不清的话「再导同一份」不触发 onChange
        e.target.value = ''
      }}
    />
  )

  const tableCard = (
    <Card>
      <CardHead
        title="产品清单"
        icon="grid"
        right={
          <div className="batchpage__acts">
            <Button variant="ghost" size="sm" icon="download" onClick={exportCsv}>
              导出 CSV
            </Button>
            <Button variant="ghost" size="sm" icon="text" onClick={exportPrompts}>
              导出提示词
            </Button>
            <Button variant="ghost" size="sm" icon="plus" onClick={addRow}>
              加一行
            </Button>
          </div>
        }
      />
      <CardBody>
        {importInput}

        {/*
          导入区 —— **这一页的第一件事**。

          2026-09-29 之前它是卡头右侧一排小按钮里的一个（「导入 CSV」），
          而下拉菜单之外的首屏全被「出图参数」占着。用户的原话是
          「如果是批量的话，这不需要了，把 CSV 的弄进来」——
          批量这一页干的活就是「一份表进来、一批任务出去」，导入才是主角。

          ⚠️ `input.batchpage__file` 仍然只此一个（CDP 与 `smoke-nav` 都指着它），
          所以卡头里那个「导入 CSV」按钮**必须删掉** —— 同一个动作两个入口，
          就是铁律「同一字段只许一个入口」要挡的那类东西。
        */}
        <div
          className={`batchpage__import${csvOver ? ' is-over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            setCsvOver(true)
          }}
          onDragLeave={() => setCsvOver(false)}
          onDrop={onCsvDrop}
        >
          <span className="batchpage__import-icon">
            <Icon name="upload" size={16} />
          </span>
          <div className="batchpage__import-text">
            <div className="batchpage__import-line">
              把 CSV 拖到这里，或者
              <button
                type="button"
                className="batchpage__import-pick"
                onClick={() => csvRef.current?.click()}
              >
                选择文件
              </button>
            </div>
            <div className="batchpage__import-sub">
              {/*
                ⚠️ 这两句跟着列一起变（2026-10-07）：
                  · 「类目 / 场景」两列 2026-09-30 随「商品套图」下架，文案里
                    却一直留着 —— 用户按它写表头，会得到两列「认不出，已忽略」；
                  · SKU 仍然在**表头清单**里（它是 CSV 的列），只是**表格里不显示**
                    了 —— 所以这里点名它「可选，标行用」，不然用户会奇怪
                    「模板里有 sku，页面上怎么找不到」。
              */}
              SKU（可选，用来标记行）/ 产品名称 / 卖点 / 参考图地址 —— 表头就是列清单。
              出图参数不放表里（同一批必须同规格），缺什么直接在行里补。
              图片不用写进表里：每行的「参考图」格能直接上传，整批图也可以直接拖到下面这张表上。
            </div>
          </div>          <Button
            variant="ghost"
            size="sm"
            icon="download"
            title="表头 + 三行示例（分别演示全填 / 场景留空 / 只填必填）"
            onClick={() => saveText('批量出图-模板.csv', batchCsvTemplate(), 'text/csv')}
          >
            下载模板
          </Button>
        </div>

        {form.fileIssues.length ? (
          <ul className="batch-issues batchpage__fileissues" style={{ marginBottom: 'var(--space-3)' }}>
            {form.fileIssues.map((x, i) => (
              <li key={`${x.field}-${i}`} data-level={x.level}>
                <span className="batch__lvl">{x.level === 'error' ? '待修' : '提示'}</span>
                {x.text}
              </li>
            ))}
          </ul>
        ) : null}
        <BatchImageDrop
          rows={rowKeys}
          onAssign={applyImages}
          disabledHint="表格里还没有可用的行 —— 先导入 CSV 或加一行，填上产品名称"
        >
          <BatchTable
            rows={parsed}
            onEdit={editRow}
            onRemove={removeRow}
            promptsFor={(row) => promptsFor(row, row.images.length)}
            onImages={(line, images) => editRow(line, { images })}
            maxImages={MAX_REFS}
          />
        </BatchImageDrop>
      </CardBody>
    </Card>
  )

  /* ------------------------------ 提交栏 ------------------------------ */

  /* 挂到 `aside.batchpage__side` 的**最顶上**（<StudioPanel> 之前），与其余 8 个
     生成页同一个位置、同一个吸顶容器。⚠️ 这一页的 `extra` 里还留着一颗
     「清空表格」—— 它是这张跨天台账的唯一清空入口，别跟着别页一起删。 */
  const submitDock = (
    <div className="studio__dock">
      <Card>
        <CardBody tight>
          <SubmitBar
            disabled={!usable.length || disabledReason !== undefined}
            disabledReason={disabledReason}
            label={submitLabel}
            onGenerate={doGenerate}
            extra={
              <div className="row row--between row--wrap" style={{ marginTop: 'var(--space-1)' }}>
                <div className="batchpage__facts text-xs muted">
                  <span>{parsed.length} 行</span>
                  <span>可用 {usable.length}</span>
                  {badCount ? <span className="batchpage__bad">待修 {badCount}</span> : null}
                  <span>已配图 {refImageTotal} 张</span>
                </div>
                {/* 这一页**保留**「清空」——见文件头「两处刻意不同」。
                    其余 8 页的清空随底部那行一起删了，这里不能跟着删：
                    导入的 CSV 是跨天台账，它是清空这张表的唯一入口。 */}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  style={{ height: 22, padding: '0 6px', flex: '0 0 auto' }}
                  onClick={() => {
                    if (window.confirm('清空这张表和所有已归位的参考图？')) reset()
                  }}
                >
                  清空表格
                </button>
              </div>
            }
          />
        </CardBody>
      </Card>
    </div>
  )

  return (
    <div className="page page--wide">
      <div className="batchpage">
        {/*
          2026-09-30 两处搬家（用户口径：「把图片这块放到表格这边做，参考图归位。
          删除把，生成放下面也可以」），以及同日稍后的一次收口
          （「参考图……可以提供图片上传」+ 把归位卡圈起来写「删除」）：

            左栏 = 参数摘要 → 导入 + 清单 → 生成   ← 一条直线，全是这一页的活
            右栏 = 任务面板（2026-10-07 起只剩它 —— 「怎么用」卡已删）

          归位那张卡**没有了**：加图现在是**每行「参考图」格自己的事**
          （`BatchTable` → `RefUploader wrap="cell"`）。原来那张卡干的事
          （拖一批图 → 按文件名归到行）没有丢，但它退成了**表格外面那层外壳**：
          拖到清单上仍然按文件名归位，认不出的仍然列出来让你指 ——
          只是不再占一张卡的版面，也不再有自己的入口按钮。
        */}
        <div className="batchpage__main">
          {paramBar}
          {tableCard}
        </div>

        <aside className="batchpage__side">
          {submitDock}
          <StudioPanel
            jobs={jobs}
            outputs={outputs}
            onCancel={cancel}
            onClear={clearResults}
            onRedo={(job: Job) => {
              patch(refillFromInputs(form, job.inputs))
              toast.success('已把这次任务的参数填回左侧表单 —— 确认后可再次生成')
            }}
            activeHint={`这一批 ${usable.length} 个产品、共 ${batchCount * usable.length} 张，可以先去别的页面干活`}
            resultTitle={`${activeName} 结果`}
            historyTitle={activeName}
            resultEmpty={`当前：${profile.name} · ${activeName}，${usable.length} 个产品 × ${batchCount} 张。确认参数后点「${submitLabel}」。`}
            aspectRatio={
              sizesInBatch.length === 1 ? sizesInBatch[0] : shots.length ? undefined : form.ratio
            }
          >
            {/*
              右栏**只剩任务面板**。
              
              2026-10-07 用户口径：把「怎么用」那张卡圈起来写「可以删除」
              （同一条消息里还说表格「宽度需要增加」）——
              右栏腾出来的高度与视线都还给结果本身，四步说明的历史使命结束：
              这一页上线两周、用户已经天天在用，那张卡成了每次都要划过去的
              一段固定文案。

              删卡**不是**删掉「先干什么」这件事 —— 那句话现在只说一遍，
              在导入区里（「把 CSV 拖到这里，或者选择文件」），
              而且是在用户真正要动手的那个位置说的。
            */}
          </StudioPanel>
        </aside>
      </div>

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />

      {/* 品牌标识只在页脚出现一次，说明这一页的「家」在哪 —— 不占侧栏第二位 */}
      <p className="batchpage__foot text-xs muted">
        <Icon name="shield" size={12} /> 表格与已归位的参考图都自动存在本机：关掉页面、明天回来接着补图。
      </p>
    </div>
  )
}
